import typia from 'typia';
import { v4 as uuid } from 'uuid';
import type { APIFieldAttributes, VisibleDataSummary, ViewIds, } from '@ardoq/api-types';
import { FilterTypes } from '@ardoq/api-types';
import type { FormattingAndLabelRules, LabelFormattingInfo, LoadedGraphWithViewpointMode, } from '@ardoq/data-model';
import { ReferenceLabelSource } from '@ardoq/data-model';
import { type Locale, localeCompare } from '@ardoq/locale';
import { getLabelFormattingFromFilterAttributes } from '@ardoq/filter-helpers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { getFieldOptionsWithDefault } from '@ardoq/perspectives-sidebar';
import { buildVisibleDataSummary } from 'canvas/aiFormatting/buildVisibleDataSummary';
import { getRestrictedMultilabelFormattingWarningMessage } from 'getRestrictedMultilabelFormattingWarningMessage';
import { isViewWithoutReferenceFormattingSupport } from 'perspectiveSidebar/perspectiveEditor/multiLabelUtils';
import { applyLabelFormattingAsOneHistoryEntry } from 'streams/perspectiveEditorData/applyFormatting';
import { labelFormattingToFilterAttributes } from 'modelInterface/filters/util';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import { VIEWPOINT_DOMAIN } from 'aiControlledCommands/viewpoint/domain';
import { WHEN_TO_USE, LABEL_CONTEXT_DESCRIPTION } from './descriptions';
import { getFieldsRepeatingRuleForAllTypes, getNewlyDuplicatedTypes, toRejectionReasons, } from './validation';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { identity, isEmpty, isEqual, uniq, uniqBy } from 'lodash';
import { staticRejection, toCommandRejection, type ShowName, type ValidationRejection, } from 'aiControlledCommands/validationRejection';
import type { AiControlledCommand } from 'aiControlledCommands/types';
// typia compiles JSDoc into the AI label tool's JSON schema - keep comments LLM-accurate.
/** A single label rule. */
export type AiLabelRule = {
    /** 'component' for component labels; 'reference' for reference labels. */
    type: 'component' | 'reference';
    /** Component or reference type name this rule applies to (e.g. 'Application'). Omit to apply to all types of this entity. */
    typeName?: string;
    /**
     * Field names to show in the label (e.g. ['cost', 'lifecycle']), taken from 'availableLabelFields' for this rule's type.
     * A type rule cannot show a field that the all-types rule already shows.
     * To show such a field differently on some types, move it from the all-types rule into a rule for
     * each type, or explain the limitation.
     */
    fields: string[];
    /**
     * Whether to prefix each value with its field name (e.g. 'Cost: High').
     * MUST be true when typeName is absent (all-types rules always show field names — the UI
     * enforces this and will override any false value). Default: true.
     * The ONLY case where false is valid: typeName IS set AND the user EXPLICITLY asks to hide
     * field names. Never infer this — when in doubt, use true.
     */
    showFieldName: boolean;
};
/**
 * Controls whether reference type names appear on reference labels.
 * Set value to 'refType' to show (default) or 'none' to hide.
 * Preserve the current value from context unless the user explicitly asks to change it.
 * If `viewLabelConstraints` indicate that reference labels cannot be created or displayed
 * in the current view, DO NOT change this rule, even if the user asks.
 * Do NOT set to 'none' when the user asks to "remove all labels" — that only affects fieldRules.
 */
export type AiReferenceMainLabelRule = {
    /** Always 'refLabel'. */
    type: FilterTypes.REFERENCE_LABEL;
    /** 'refType': show reference type names (default). 'none': hide them. */
    value: ReferenceLabelSource.REFERENCE_TYPE | ReferenceLabelSource.NONE;
};
export type ApplyLabelFormattingPayload = {
    /**
     * The COMPLETE set of extra field-value label rules to apply — not a delta.
     *
     * Payload composition rules:
     * Start from `currentFieldRules` by default.
     * Keep every existing rule unless the user's request requires adding, removing, or changing it.
     * Return the full final rule set, not just the changed rules.
     *
     * What these rules do:
     * These rules only ADD extra field-value labels on components and references.
     * They never replace built-in labels.
     *
     * Important built-in labels:
     * Every component always shows its name, and this tool cannot hide or change that.
     * Reference type name visibility is controlled separately by `referenceMainLabelRule`, not by `rules`.
     *
     * Removal semantics:
     * An empty array removes all extra field-value labels.
     * It does NOT remove the built-in component name label.
     * It does NOT by itself change reference type name visibility.
     *
     * Rule shape:
     * A rule without `typeName` is global and applies to ALL types of that entity.
     * A rule with `typeName` applies only to that specific component or reference type.
     * Prefer a single global rule when the user wants the same label on all components or all references.
     * At most one rule per `type` + `typeName`; to show several fields on a type, list them all in that rule's `fields`.
     * Types that already have several rules in `currentFieldRules` may keep them.
     */
    rules: AiLabelRule[];
    /**
     * Controls whether the built-in reference type name is shown on reference labels.
     * `refType` = show reference type names
     * `none` = hide reference type names
     *
     * HARD RULES FOR PAYLOAD COMPOSITION:
     * This field is always required.
     * Start with the exact value from `currentReferenceMainLabelRule`.
     * Copy that value unchanged unless the user explicitly asks to show or hide reference type names.
     * If `viewLabelConstraints` is present and indicates that reference labels are unavailable,
     * unsupported, or cannot be changed in the current view, you MUST copy
     * `currentReferenceMainLabelRule` unchanged.
     * In that case, changing this field is INVALID, even if the user explicitly asks.
     * Requests like "remove all labels", "clear labels", or "hide labels" affect `rules` only
     * and MUST NOT change this field.
     */
    referenceMainLabelRule: AiReferenceMainLabelRule;
};
export type LabelFieldOption = {
    name: string;
    label: string;
};
type LabelFieldsOfType = {
    typeName: string;
    fields: LabelFieldOption[];
};
type EntityLabelFields = {
    builtInFields: LabelFieldOption[];
    customFields: LabelFieldOption[];
    fieldNamesByType: Record<string, string[]>;
};
export type AvailableLabelFields = {
    component: EntityLabelFields;
    reference: EntityLabelFields;
};
const getBuiltInLabelFields = (isComponent: boolean): LabelFieldOption[] => {
    const grouped = getFieldOptionsWithDefault([], isComponent);
    const defaultGroup = grouped[0];
    if (!defaultGroup || !('options' in defaultGroup))
        return [];
    return defaultGroup.options.map(option => ({
        name: option.value,
        label: option.label,
    }));
};
const toLabelFieldOptions = (fields: Record<string, APIFieldAttributes>): LabelFieldOption[] => Object.values(fields).map(({ name, label }) => ({ name, label }));
const collectComponentTypeFields = ({ scopeComponentIds = [], scopeData, }: LoadedGraphWithViewpointMode): LabelFieldsOfType[] => {
    const typeFieldsByKey = new Map<string, LabelFieldsOfType>();
    for (const id of scopeComponentIds) {
        const component = enhancedScopeDataOperations.getComponent(scopeData, id);
        if (!component)
            continue;
        const componentType = enhancedScopeDataOperations.getComponentTypeByComponentId(scopeData, id);
        if (!componentType)
            continue;
        const typeKey = `${component.model}:${componentType.id}`;
        if (typeFieldsByKey.has(typeKey))
            continue;
        typeFieldsByKey.set(typeKey, {
            typeName: componentType.name,
            fields: toLabelFieldOptions(enhancedScopeDataOperations.getComponentFields(scopeData, id)),
        });
    }
    return [...typeFieldsByKey.values()];
};
const collectReferenceTypeFields = ({ scopeReferenceIds = [], scopeData, }: LoadedGraphWithViewpointMode): LabelFieldsOfType[] => {
    const typeFieldsByKey = new Map<string, LabelFieldsOfType>();
    for (const id of scopeReferenceIds) {
        const reference = enhancedScopeDataOperations.getReference(scopeData, id);
        if (!reference)
            continue;
        const referenceType = enhancedScopeDataOperations.getReferenceTypeByReferenceId(scopeData, id);
        if (!referenceType)
            continue;
        const typeKey = `${reference.model}:${referenceType.id}`;
        if (typeFieldsByKey.has(typeKey))
            continue;
        typeFieldsByKey.set(typeKey, {
            typeName: referenceType.name,
            fields: toLabelFieldOptions(enhancedScopeDataOperations.getReferenceFields(scopeData, id)),
        });
    }
    return [...typeFieldsByKey.values()];
};
const toFieldNamesByType = (typeFields: LabelFieldsOfType[]): Record<string, string[]> => Object.fromEntries(typeFields.reduce((fieldNamesByType, { typeName, fields }) => fieldNamesByType.set(typeName, uniq([
    ...(fieldNamesByType.get(typeName) ?? []),
    ...fields.map(({ name }) => name),
          ])), new Map<string, string[]>()));
type ToEntityLabelFieldsArgs = {
    builtInFields: LabelFieldOption[];
    typeFields: LabelFieldsOfType[];
    locale: Locale;
};
const toEntityLabelFields = ({ builtInFields, typeFields, locale, }: ToEntityLabelFieldsArgs): EntityLabelFields => ({
    builtInFields,
    customFields: uniqBy(typeFields.flatMap(({ fields }) => fields), 'name').toSorted((a, b) => localeCompare(a.label, b.label, locale)),
    fieldNamesByType: toFieldNamesByType(typeFields),
});
const buildAvailableLabelFields = (loadedGraph: LoadedGraphWithViewpointMode, locale: Locale): AvailableLabelFields => ({
    component: toEntityLabelFields({
        builtInFields: getBuiltInLabelFields(true),
        typeFields: collectComponentTypeFields(loadedGraph),
        locale,
    }),
    reference: toEntityLabelFields({
        builtInFields: getBuiltInLabelFields(false),
        typeFields: collectReferenceTypeFields(loadedGraph),
        locale,
    }),
});
export type ApplyLabelFormattingContext = {
    summary: VisibleDataSummary;
    availableLabelFields: AvailableLabelFields;
    currentFieldRules: AiLabelRule[];
    currentReferenceMainLabelRule: AiReferenceMainLabelRule;
    viewLabelConstraints?: string;
};
export const toAiLabelRule = (info: LabelFormattingInfo): AiLabelRule => ({
    type: info.type,
    ...(info.typeName !== null && { typeName: info.typeName }),
    fields: info.fields,
    showFieldName: info.showFieldName,
});
export const buildLabelFormattingCommand = (loadedGraph: LoadedGraphWithViewpointMode, { rules }: FormattingAndLabelRules, mainViewId: ViewIds, locale: Locale): AiControlledCommand<typeof labelFormattingCommandDefinition> => {
    const viewLabelConstraints = getRestrictedMultilabelFormattingWarningMessage(mainViewId);
    const currentReferenceMainLabelValue = rules.find(rule => rule.type === FilterTypes.REFERENCE_LABEL)?.value ===
        ReferenceLabelSource.NONE
        ? ReferenceLabelSource.NONE
        : ReferenceLabelSource.REFERENCE_TYPE;
    const context: ApplyLabelFormattingContext = {
        summary: buildVisibleDataSummary(loadedGraph),
        availableLabelFields: buildAvailableLabelFields(loadedGraph, locale),
        currentFieldRules: getLabelFormattingFromFilterAttributes(rules).map(toAiLabelRule),
        currentReferenceMainLabelRule: {
            type: FilterTypes.REFERENCE_LABEL,
            value: currentReferenceMainLabelValue,
        },
        ...(viewLabelConstraints && { viewLabelConstraints }),
    };
    return { ...labelFormattingCommandDefinition, context };
};
const toLabelFormattingInfo = (rule: AiLabelRule): LabelFormattingInfo => ({
    ...rule,
    fields: rule.fields,
    typeName: rule.typeName ?? null,
    __id: uuid(),
});
const roundTripThroughFilters = (rules: AiLabelRule[]): AiLabelRule[] => getLabelFormattingFromFilterAttributes(rules.map(toLabelFormattingInfo).flatMap(labelFormattingToFilterAttributes)).map(toAiLabelRule);
const hasNewReferenceRule = (rules: AiLabelRule[], currentFieldRules: AiLabelRule[]): boolean => rules.some(rule => rule.type === 'reference' &&
    !currentFieldRules.some(currentRule => isEqual(currentRule, rule)));
export const executeApplyLabelFormatting = (payload: ApplyLabelFormattingPayload): void => {
    const showReferenceType = payload.referenceMainLabelRule.value !== ReferenceLabelSource.NONE;
    applyLabelFormattingAsOneHistoryEntry(payload.rules.map(toLabelFormattingInfo), showReferenceType);
};
type FindLabelFormattingRejectionArgs = {
    rawPayload: ApplyLabelFormattingPayload;
    rules: AiLabelRule[];
    context: ApplyLabelFormattingContext;
    activeViewId: ViewIds;
};
const findLabelFormattingRejection = ({ rawPayload, rules, context: { availableLabelFields, currentFieldRules, currentReferenceMainLabelRule, }, activeViewId, }: FindLabelFormattingRejectionArgs): ValidationRejection | undefined => {
    if (isViewWithoutReferenceFormattingSupport(activeViewId) &&
        (hasNewReferenceRule(rules, currentFieldRules) ||
            rawPayload.referenceMainLabelRule.value !==
                currentReferenceMainLabelRule.value)) {
        return staticRejection('referenceLabelsNotShown', "Reference labels are not shown in the current view, so reference rules can't be added or changed here and 'currentReferenceMainLabelRule' must stay unchanged. Keep the remaining reference rules from 'currentFieldRules' as they are.");
    }
    if (isEmpty(availableLabelFields.reference.fieldNamesByType) &&
        hasNewReferenceRule(rules, currentFieldRules)) {
        return staticRejection('noReferencesInView', "There are no references in the view, so reference rules can't be added or changed. Keep the remaining reference rules from 'currentFieldRules' as they are.");
    }
    if (getFieldsRepeatingRuleForAllTypes(rawPayload.rules, identity).length) {
        return {
            kind: 'fieldRepeatsRuleForAllTypes',
            buildReason: showName => `A field can't be in both the all-types rule and a type rule: ${getFieldsRepeatingRuleForAllTypes(rawPayload.rules, showName).join(', ')}. To show such a field differently on some types, it has to move from the all-types rule into a rule for each type. Explain the limitation and ask the user which option they want before changing any rules.`,
        };
    }
    const toPerRuleReasons = (showName: ShowName): string[] => rules.flatMap(toRejectionReasons(availableLabelFields, currentFieldRules, showName));
    if (toPerRuleReasons(identity).length) {
        return {
            kind: 'invalidRule',
            buildReason: showName => `${toPerRuleReasons(showName).join(' ')} Use only the field names 'availableLabelFields' lists for the rule's type.`,
        };
    }
    if (getNewlyDuplicatedTypes(rules, currentFieldRules, identity).length) {
        return {
            kind: 'duplicatedTypeRule',
            buildReason: showName => `Each type can have only one rule. Merge the rules for ${getNewlyDuplicatedTypes(rules, currentFieldRules, showName).join(', ')} into one, or ask the user whether field names should be shown on them.`,
        };
    }
    return undefined;
};
export const labelFormattingCommandDefinition = defineAiControlledCommand<ApplyLabelFormattingPayload, ApplyLabelFormattingContext>({
    name: AiControlledCommandId.APPLY_LABEL_FORMATTING,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                ApplyLabelFormattingPayload: {
                    type: "object",
                    properties: {
                        rules: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/AiLabelRule"
                            },
                            description: "The COMPLETE set of extra field-value label rules to apply \u2014 not a delta.\n\nPayload composition rules:\nStart from `currentFieldRules` by default.\nKeep every existing rule unless the user's request requires adding, removing, or changing it.\nReturn the full final rule set, not just the changed rules.\n\nWhat these rules do:\nThese rules only ADD extra field-value labels on components and references.\nThey never replace built-in labels.\n\nImportant built-in labels:\nEvery component always shows its name, and this tool cannot hide or change that.\nReference type name visibility is controlled separately by `referenceMainLabelRule`, not by `rules`.\n\nRemoval semantics:\nAn empty array removes all extra field-value labels.\nIt does NOT remove the built-in component name label.\nIt does NOT by itself change reference type name visibility.\n\nRule shape:\nA rule without `typeName` is global and applies to ALL types of that entity.\nA rule with `typeName` applies only to that specific component or reference type.\nPrefer a single global rule when the user wants the same label on all components or all references.\nAt most one rule per `type` + `typeName`; to show several fields on a type, list them all in that rule's `fields`.\nTypes that already have several rules in `currentFieldRules` may keep them."
                        },
                        referenceMainLabelRule: {
                            $ref: "#/components/schemas/AiReferenceMainLabelRule",
                            description: "Controls whether the built-in reference type name is shown on reference labels.\n`refType` = show reference type names\n`none` = hide reference type names\n\nHARD RULES FOR PAYLOAD COMPOSITION:\nThis field is always required.\nStart with the exact value from `currentReferenceMainLabelRule`.\nCopy that value unchanged unless the user explicitly asks to show or hide reference type names.\nIf `viewLabelConstraints` is present and indicates that reference labels are unavailable,\nunsupported, or cannot be changed in the current view, you MUST copy\n`currentReferenceMainLabelRule` unchanged.\nIn that case, changing this field is INVALID, even if the user explicitly asks.\nRequests like \"remove all labels\", \"clear labels\", or \"hide labels\" affect `rules` only\nand MUST NOT change this field."
                        }
                    },
                    required: [
                        "rules",
                        "referenceMainLabelRule"
                    ]
                },
                AiLabelRule: {
                    type: "object",
                    properties: {
                        type: {
                            oneOf: [
                                {
                                    "const": "component"
                                },
                                {
                                    "const": "reference"
                                }
                            ],
                            description: "'component' for component labels; 'reference' for reference labels."
                        },
                        typeName: {
                            type: "string",
                            description: "Component or reference type name this rule applies to (e.g. 'Application'). Omit to apply to all types of this entity."
                        },
                        fields: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Field names to show in the label (e.g. ['cost', 'lifecycle']), taken from 'availableLabelFields' for this rule's type.\nA type rule cannot show a field that the all-types rule already shows.\nTo show such a field differently on some types, move it from the all-types rule into a rule for\neach type, or explain the limitation."
                        },
                        showFieldName: {
                            type: "boolean",
                            description: "Whether to prefix each value with its field name (e.g. 'Cost: High').\nMUST be true when typeName is absent (all-types rules always show field names \u2014 the UI\nenforces this and will override any false value). Default: true.\nThe ONLY case where false is valid: typeName IS set AND the user EXPLICITLY asks to hide\nfield names. Never infer this \u2014 when in doubt, use true."
                        }
                    },
                    required: [
                        "type",
                        "fields",
                        "showFieldName"
                    ],
                    description: "A single label rule."
                },
                AiReferenceMainLabelRule: {
                    type: "object",
                    properties: {
                        type: {
                            "const": "refLabel",
                            description: "Always 'refLabel'."
                        },
                        value: {
                            oneOf: [
                                {
                                    "const": "none"
                                },
                                {
                                    "const": "refType"
                                }
                            ],
                            description: "'refType': show reference type names (default). 'none': hide them."
                        }
                    },
                    required: [
                        "type",
                        "value"
                    ],
                    description: "Controls whether reference type names appear on reference labels.\nSet value to 'refType' to show (default) or 'none' to hide.\nPreserve the current value from context unless the user explicitly asks to change it.\nIf `viewLabelConstraints` indicate that reference labels cannot be created or displayed\nin the current view, DO NOT change this rule, even if the user asks.\nDo NOT set to 'none' when the user asks to \"remove all labels\" \u2014 that only affects fieldRules."
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/ApplyLabelFormattingPayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: LABEL_CONTEXT_DESCRIPTION,
    isContext: (context): context is ApplyLabelFormattingContext => (() => { const _iv5 = new Set(["Checkbox", "Date", "DateOnlyRange", "DateTime", "DateTimeRange", "Email", "File", "List", "Number", "SelectMultipleList", "Text", "TextArea", "Url", "User"]); const _iv7 = new Set(["idMatch", "dateMatch", "dateEarlier", "dateLater", "boolean", "equals", "lessThan", "greaterThan", "contains", "dateRangeContains", "dateRangeDoesNotContain", "dateRangeBefore", "dateRangeAfter", "colorRange", "idDoesNotMatch", "notEqual", "notContains", "notBoolean", "greaterThanOrEqualTo", "lessThanOrEqualTo", "between", "notBetween", "dateBetween", "dateNotBetween", "dateNotEqual", "dateOnOrBefore", "dateOnOrAfter", "dateRangeOnOrBefore", "dateRangeOnOrAfter", "dateRangeBetween", "relativeDate", "isEmpty", "isNotEmpty", "listContains", "listDoesNotContain"]); const _io0 = (input: any): boolean => "object" === typeof input.summary && null !== input.summary && _io1(input.summary) && ("object" === typeof input.availableLabelFields && null !== input.availableLabelFields && _io10(input.availableLabelFields)) && (Array.isArray(input.currentFieldRules) && input.currentFieldRules.every((elem: any) => "object" === typeof elem && null !== elem && _io14(elem))) && ("object" === typeof input.currentReferenceMainLabelRule && null !== input.currentReferenceMainLabelRule && _io15(input.currentReferenceMainLabelRule)) && (undefined === input.viewLabelConstraints || "string" === typeof input.viewLabelConstraints); const _io1 = (input: any): boolean => Array.isArray(input.componentTypes) && input.componentTypes.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem)) && (Array.isArray(input.referenceTypes) && input.referenceTypes.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem))); const _io2 = (input: any): boolean => "string" === typeof input.typeName && "number" === typeof input.instanceCount && (Array.isArray(input.fields) && input.fields.every((elem: any) => "object" === typeof elem && null !== elem && _io3(elem))); const _io3 = (input: any): boolean => "string" === typeof input.name && true === _iv5.has(input.type) && "number" === typeof input.presentCount && "number" === typeof input.nullCount && ("object" === typeof input.distribution && null !== input.distribution && _iu0(input.distribution)) && (Array.isArray(input.allowedComparators) && input.allowedComparators.every((elem: any) => true === _iv7.has(elem))); const _io4 = (input: any): boolean => "enum" === input.kind && (Array.isArray(input.topValues) && input.topValues.every((elem: any) => "object" === typeof elem && null !== elem && _io5(elem))) && "number" === typeof input.distinctCount; const _io5 = (input: any): boolean => "string" === typeof input.value && "number" === typeof input.count; const _io6 = (input: any): boolean => "numeric" === input.kind && "number" === typeof input.min && "number" === typeof input.max && "number" === typeof input.mean; const _io7 = (input: any): boolean => "date" === input.kind && "string" === typeof input.min && "string" === typeof input.max; const _io8 = (input: any): boolean => "text" === input.kind && "number" === typeof input.avgLength && "number" === typeof input.blankCount; const _io9 = (input: any): boolean => "opaque" === input.kind; const _io10 = (input: any): boolean => "object" === typeof input.component && null !== input.component && _io11(input.component) && ("object" === typeof input.reference && null !== input.reference && _io11(input.reference)); const _io11 = (input: any): boolean => Array.isArray(input.builtInFields) && input.builtInFields.every((elem: any) => "object" === typeof elem && null !== elem && _io12(elem)) && (Array.isArray(input.customFields) && input.customFields.every((elem: any) => "object" === typeof elem && null !== elem && _io12(elem))) && ("object" === typeof input.fieldNamesByType && null !== input.fieldNamesByType && false === Array.isArray(input.fieldNamesByType) && _io13(input.fieldNamesByType)); const _io12 = (input: any): boolean => "string" === typeof input.name && "string" === typeof input.label; const _io13 = (input: any): boolean => Object.keys(input).every((key: any) => {
        const value = input[key];
        if (undefined === value)
            return true;
        return Array.isArray(value) && value.every((elem: any) => "string" === typeof elem);
    }); const _io14 = (input: any): boolean => ("component" === input.type || "reference" === input.type) && (undefined === input.typeName || "string" === typeof input.typeName) && (Array.isArray(input.fields) && input.fields.every((elem: any) => "string" === typeof elem)) && "boolean" === typeof input.showFieldName; const _io15 = (input: any): boolean => "refLabel" === input.type && ("none" === input.value || "refType" === input.value); const _iu0 = (input: any): any => (() => {
        if ("enum" === input.kind)
            return _io4(input);
        else if ("numeric" === input.kind)
            return _io6(input);
        else if ("date" === input.kind)
            return _io7(input);
        else if ("text" === input.kind)
            return _io8(input);
        else if ("opaque" === input.kind)
            return _io9(input);
        else
            return false;
    })(); return (input: any): input is ApplyLabelFormattingContext => "object" === typeof input && null !== input && _io0(input); })()(context),
    validate: (rawPayload, context, { activeViewId }) => {
        if (!(() => { const _io0 = (input: any): boolean => Array.isArray(input.rules) && input.rules.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)) && ("object" === typeof input.referenceMainLabelRule && null !== input.referenceMainLabelRule && _io2(input.referenceMainLabelRule)); const _io1 = (input: any): boolean => ("component" === input.type || "reference" === input.type) && (undefined === input.typeName || "string" === typeof input.typeName) && (Array.isArray(input.fields) && input.fields.every((elem: any) => "string" === typeof elem)) && "boolean" === typeof input.showFieldName; const _io2 = (input: any): boolean => "refLabel" === input.type && ("none" === input.value || "refType" === input.value); return (input: any): input is ApplyLabelFormattingPayload => "object" === typeof input && null !== input && _io0(input); })()(rawPayload)) {
            return {
                status: 'rejected',
                reason: 'The AI returned invalid label formatting.',
            };
        }
        const rules = roundTripThroughFilters(rawPayload.rules);
        const rejection = findLabelFormattingRejection({
            rawPayload,
            rules,
            context,
            activeViewId,
        });
        if (rejection) {
            return toCommandRejection(rejection);
        }
        return { status: 'valid', payload: { ...rawPayload, rules } };
    },
    execute: executeApplyLabelFormatting,
    isApplied: (payload, context) => isEqual(payload.rules, context.currentFieldRules) &&
        isEqual(payload.referenceMainLabelRule, context.currentReferenceMainLabelRule),
});
